/**
 * ORCA360 Part C - Competency Assessment Component
 * Displays 9 competencies with grades, OB codes, and evidence
 */

import React, { useState } from 'react';

export interface ObservableBehavior {
  code: string;
  description: string;
}

export interface CompetencyEvidence {
  ob: ObservableBehavior;
  comment: string;
  quote?: string;
  timestamp?: string;
  validationStatus?: string;
}

export interface CompetencyAssessment {
  competency: string;
  competencyName: string;
  grade: number;
  observableBehaviors: CompetencyEvidence[];
  instructorComments?: string;
}

export interface PartCData {
  assessments: CompetencyAssessment[];
  gradeLegend: Record<number, string>;
}

interface Props {
  data: PartCData;
}

// Phase 5d — canonical render order per WingMentor CBTA/EBT Core Framework
// 2.0 and Andre's Section 7 model. Used by the sort below to order Part C
// rows in the online viewer to match the PDF / DOCX output.
const COMPETENCY_ORDER = ['KNO', 'FPM', 'PRO', 'FPA', 'COM', 'WLM', 'PSD', 'LTW', 'SAW'];

const COMPETENCY_LABELS: Record<string, { number: number; name: string }> = {
  KNO: { number: 1, name: 'Knowledge (KNO)' },
  FPM: { number: 2, name: 'Flight Path Mgmt - Manual (FPM)' },
  PRO: { number: 3, name: 'Application of Procedures (PRO)' },
  FPA: { number: 4, name: 'Flight Path Mgmt - Auto (FPA)' },
  COM: { number: 5, name: 'Communication (COM)' },
  WLM: { number: 6, name: 'Workload Management (WLM)' },
  PSD: { number: 7, name: 'Prob. Solving & Decision Making (PSD)' },
  LTW: { number: 8, name: 'Leadership & Teamwork (LTW)' },
  SAW: { number: 9, name: 'Situation Awareness (SAW)' },
};

export function ORCA360PartC({ data }: Props) {
  const [expandedCompetency, setExpandedCompetency] = useState<string | null>(null);

  const getGradeColor = (grade: number): string => {
    switch (grade) {
      case 5:
        return 'text-green-700 bg-green-100 border-green-300';
      case 4:
        return 'text-blue-700 bg-blue-100 border-blue-300';
      case 3:
        return 'text-gray-700 bg-gray-100 border-gray-300';
      case 2:
        return 'text-orange-700 bg-orange-100 border-orange-300';
      case 1:
        return 'text-red-700 bg-red-100 border-red-300';
      default:
        return 'text-gray-700 bg-gray-100 border-gray-300';
    }
  };

  const getGradeLabel = (grade: number): string => {
    return data.gradeLegend[grade] || `Grade ${grade}`;
  };

  // Sort assessments by ORCA360 order
  const sortedAssessments = [...data.assessments].sort((a, b) => {
    const orderA = COMPETENCY_ORDER.indexOf(a.competency);
    const orderB = COMPETENCY_ORDER.indexOf(b.competency);
    return orderA - orderB;
  });

  return (
    <div className="bg-white sm:rounded-lg sm:shadow-md p-2 sm:p-6 border-y sm:border border-slate-200 sm:border-gray-200">
      <h2 className="text-base sm:text-lg font-bold text-navy mb-2 border-b border-gray-200 pb-2">
        Part C — Competency Assessment
      </h2>

      {/* Grade Legend */}
      <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-gray-50 rounded flex flex-wrap gap-1.5 sm:gap-3 text-[10px] sm:text-xs">
        {Object.entries(data.gradeLegend).reverse().map(([grade, label]) => (
          <span key={grade} className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded ${getGradeColor(Number(grade))}`}>
            {grade}={label}
          </span>
        ))}
      </div>

      {/* Desktop: Competency Table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600 border-b w-1/3">
                Pilot Core Competency
              </th>
              <th className="px-3 py-2 text-center font-medium text-gray-600 border-b w-16">
                Grade (1-5)
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 border-b w-1/4">
                Observable Behaviors (OBs)
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">
                Evidence & Instructor Comments
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedAssessments.map((assessment) => {
              const compInfo = COMPETENCY_LABELS[assessment.competency];
              const isExpanded = expandedCompetency === assessment.competency;

              return (
                <React.Fragment key={assessment.competency}>
                  <tr
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedCompetency(isExpanded ? null : assessment.competency)}
                  >
                    <td className="px-3 py-2">
                      <div className="font-medium text-navy">
                        {compInfo?.number || ''}. {compInfo?.name || assessment.competencyName}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full font-bold border ${getGradeColor(assessment.grade)}`}>
                        {assessment.grade}
                      </span>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {getGradeLabel(assessment.grade)}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {assessment.observableBehaviors.length > 0 ? (
                        <div className="space-y-1">
                          {assessment.observableBehaviors.slice(0, 2).map((ob, idx) => (
                            <div key={idx} className="text-xs">
                              <span className="font-medium text-blue-700">{ob.ob.code}</span>
                              <span className="text-gray-500 ml-1 truncate">{ob.ob.description}</span>
                            </div>
                          ))}
                          {assessment.observableBehaviors.length > 2 && (
                            <span className="text-xs text-gray-400">
                              +{assessment.observableBehaviors.length - 2} more OBs
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No OBs recorded</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {assessment.observableBehaviors.length > 0 ? (
                        <div className="text-xs text-gray-700 truncate">
                          {assessment.observableBehaviors[0]?.comment || 'No comments'}
                        </div>
                      ) : assessment.instructorComments ? (
                        <div className="text-xs text-gray-700 truncate">
                          {assessment.instructorComments}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No evidence</span>
                      )}
                      <span className="text-xs text-blue-600 mt-1 block">
                        {isExpanded ? '▼ Click to collapse' : '▶ Click to expand'}
                      </span>
                    </td>
                  </tr>

                  {/* Expanded Detail Row */}
                  {isExpanded && (
                    <tr className="bg-gray-50">
                      <td colSpan={4} className="px-6 py-4">
                        <div className="space-y-3">
                          <h4 className="font-medium text-navy">Observable Behaviors & Evidence</h4>

                          {assessment.observableBehaviors.length > 0 ? (
                            <div className="space-y-3">
                              {assessment.observableBehaviors.map((ob, idx) => (
                                <div key={idx} className="p-3 bg-white rounded border border-gray-200">
                                  <div className="flex items-start justify-between mb-2">
                                    <div>
                                      <span className="font-medium text-blue-700">{ob.ob.code}:</span>
                                      <span className="text-gray-600 ml-2">{ob.ob.description}</span>
                                    </div>
                                    {ob.timestamp && (
                                      <span className="text-xs text-gray-400">@ {ob.timestamp}</span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-700 mb-2">{ob.comment}</p>
                                  {ob.quote && (
                                    <div className="bg-gray-50 p-2 rounded border-l-4 border-blue-300 text-sm italic text-gray-600 line-clamp-3">
                                      "{ob.quote}"
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">No detailed evidence recorded</p>
                          )}

                          {assessment.instructorComments && (
                            <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
                              <h5 className="font-medium text-yellow-800 text-sm mb-1">Instructor Comments</h5>
                              <p className="text-sm text-gray-700">{assessment.instructorComments}</p>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile: Competency Cards */}
      <div className="sm:hidden space-y-1.5">
        {sortedAssessments.map((assessment) => {
          const compInfo = COMPETENCY_LABELS[assessment.competency];
          const isExpanded = expandedCompetency === assessment.competency;

          return (
            <div
              key={assessment.competency}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              <button
                onClick={() => setExpandedCompetency(isExpanded ? null : assessment.competency)}
                className="w-full text-left p-2 touch-target"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0 mr-2">
                    <div className="text-sm font-medium text-navy truncate">
                      {compInfo?.number}. {compInfo?.name || assessment.competencyName}
                    </div>
                  </div>
                  <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold border ${getGradeColor(assessment.grade)}`}>
                    {assessment.grade}
                  </span>
                </div>
                {!isExpanded && assessment.observableBehaviors.length > 0 && (
                  <div className="mt-1 text-xs text-gray-500 truncate">
                    <span className="font-medium text-blue-700">{assessment.observableBehaviors[0]?.ob.code}</span>
                    {' '}{assessment.observableBehaviors[0]?.comment}
                  </div>
                )}
                <div className="text-[10px] text-blue-600 mt-0.5">
                  {isExpanded ? '▼ Collapse' : '▶ Expand details'}
                </div>
              </button>

              {isExpanded && (
                <div className="px-2 pb-2 border-t border-gray-100 space-y-2">
                  <div className="text-xs text-gray-500 pt-1">
                    {getGradeLabel(assessment.grade)}
                  </div>

                  {assessment.observableBehaviors.length > 0 ? (
                    assessment.observableBehaviors.map((ob, idx) => (
                      <div key={idx} className="p-1.5 bg-gray-50 rounded border border-gray-200">
                        <div className="text-xs">
                          <span className="font-medium text-blue-700">{ob.ob.code}:</span>
                          <span className="text-gray-600 ml-1">{ob.ob.description}</span>
                        </div>
                        <p className="text-xs text-gray-700 mt-1">{ob.comment}</p>
                        {ob.quote && (
                          <div className="mt-1 pl-2 border-l-2 border-blue-300 text-xs italic text-gray-600 line-clamp-3">
                            "{ob.quote}"
                          </div>
                        )}
                        {ob.timestamp && (
                          <div className="text-[10px] text-gray-400 mt-0.5">@ {ob.timestamp}</div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-500 italic">No detailed evidence recorded</p>
                  )}

                  {assessment.instructorComments && (
                    <div className="p-1.5 bg-yellow-50 rounded border border-yellow-200">
                      <p className="text-xs font-medium text-yellow-800 mb-0.5">Instructor Comments</p>
                      <p className="text-xs text-gray-700">{assessment.instructorComments}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ORCA360PartC;
